import type { Edge, Node } from '@xyflow/react'
import { Memory } from '../hooks/use-memories'

export const MEMORY_NODE_WIDTH = 180
export const MEMORY_NODE_HEIGHT = 68
export const MEMORY_NODE_HEIGHT_WITH_SOURCE = 96

export function getMemoryNodeHeight(memory: Memory): number {
  return memory.source_message_text?.trim()
    ? MEMORY_NODE_HEIGHT_WITH_SOURCE
    : MEMORY_NODE_HEIGHT
}
export const ENTITY_NODE_WIDTH = 120
export const ENTITY_NODE_HEIGHT = 32

export type MemoryNodeData = {
  label: string
  memory: Memory
  memory_type: string
  importance: string
}

export type EntityNodeData = {
  label: string
  entity_type: string
}

export type RelationEdgeData = {
  label?: string
  relationship_type?: string
  relation_type?: string
  strength?: number
  isEntityEdge?: boolean
}

export interface FlowGraphData {
  nodes: Node<MemoryNodeData | EntityNodeData>[]
  edges: Edge<RelationEdgeData>[]
}

/**
 * Transform memories and their relationships into React Flow graph format.
 */
export function transformMemoriesToFlow(memories: Memory[]): FlowGraphData {
  const nodes: Node<MemoryNodeData | EntityNodeData>[] = []
  const edges: Edge<RelationEdgeData>[] = []
  const nodeIds = new Set<string>()
  const edgeIds = new Set<string>()
  const memoryIdSet = new Set(memories.map((m) => m.id))

  memories.forEach((memory) => {
    const nodeId = `memory-${memory.id}`
    if (!nodeIds.has(nodeId)) {
      nodes.push({
        id: nodeId,
        type: 'memory',
        position: { x: 0, y: 0 },
        data: {
          label: truncateText(memory.content, 50),
          memory,
          memory_type: memory.memory_type,
          importance: memory.importance,
        },
      })
      nodeIds.add(nodeId)
    }

    if (memory.relations && memory.relations.length > 0) {
      memory.relations.forEach((relation) => {
        if (relation.direction !== 'outgoing') return
        if (!memoryIdSet.has(relation.memory_id)) return

        const targetId = `memory-${relation.memory_id}`
        const edgeId = `${nodeId}->${targetId}:${relation.relation_type}`
        if (edgeIds.has(edgeId)) return

        edges.push({
          id: edgeId,
          source: nodeId,
          target: targetId,
          type: 'relation',
          data: {
            label: relation.relation_type,
            relation_type: relation.relation_type,
            strength: relation.strength,
          },
        })
        edgeIds.add(edgeId)
      })
    } else if (memory.related_memories && memory.related_memories.length > 0) {
      memory.related_memories.forEach((relatedId) => {
        const targetId = `memory-${relatedId}`
        const edgeId = `${nodeId}->${targetId}:related`
        if (!memoryIdSet.has(relatedId) || edgeIds.has(edgeId)) return

        edges.push({
          id: edgeId,
          source: nodeId,
          target: targetId,
          type: 'relation',
          data: {
            label: 'related',
            relation_type: 'related',
            strength: 0.5,
          },
        })
        edgeIds.add(edgeId)
      })
    }

    if (memory.entities && memory.entities.length > 0) {
      memory.entities.forEach((entity) => {
        const entityNodeId = `entity-${entity.id}`

        if (!nodeIds.has(entityNodeId)) {
          nodes.push({
            id: entityNodeId,
            type: 'entity',
            position: { x: 0, y: 0 },
            data: {
              label: entity.name,
              entity_type: entity.entity_type,
            },
          })
          nodeIds.add(entityNodeId)
        }

        const edgeId = `${nodeId}->${entityNodeId}:${entity.relationship_type}`
        if (!edgeIds.has(edgeId)) {
          edges.push({
            id: edgeId,
            source: nodeId,
            target: entityNodeId,
            type: 'relation',
            data: {
              label: entity.relationship_type,
              relationship_type: entity.relationship_type,
              isEntityEdge: true,
            },
          })
          edgeIds.add(edgeId)
        }
      })
    }
  })

  return { nodes, edges }
}

export function getNodeSize(importance?: string): number {
  switch (importance) {
    case 'high':
      return 40
    case 'medium':
      return 30
    case 'low':
      return 20
    default:
      return 25
  }
}

export function getMemoryNodeColor(memoryType?: string): string {
  switch (memoryType) {
    case 'factual':
      return '#3b82f6'
    case 'episodic':
      return '#a855f7'
    case 'semantic':
      return '#6366f1'
    default:
      return '#6b7280'
  }
}

export function getMemoryNodeBorderClass(memoryType?: string): string {
  switch (memoryType) {
    case 'factual':
      return 'border-blue-500'
    case 'episodic':
      return 'border-purple-500'
    case 'semantic':
      return 'border-indigo-500'
    default:
      return 'border-muted-foreground/40'
  }
}

export function getEntityNodeColor(entityType?: string): string {
  switch (entityType) {
    case 'person':
      return '#ef4444'
    case 'place':
      return '#10b981'
    case 'concept':
      return '#f59e0b'
    case 'organisation':
      return '#8b5cf6'
    default:
      return '#64748b'
  }
}

export function getEdgeColor(relationshipType?: string, relationType?: string): string {
  const type = relationshipType || relationType
  switch (type) {
    case 'related':
      return '#94a3b8'
    case 'causes':
      return '#f87171'
    case 'follows':
      return '#60a5fa'
    case 'confirms':
      return '#34d399'
    case 'contradicts':
      return '#fbbf24'
    case 'pattern':
      return '#c084fc'
    case 'mentions':
    case 'describes':
    case 'references':
    case 'about':
    case 'involves':
      return '#a78bfa'
    default:
      return '#cbd5e1'
  }
}

export function getEdgeWidth(strength?: number): number {
  if (!strength) return 2
  return Math.max(1, Math.min(5, strength * 5))
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength - 3) + '...'
}
